// frontend/src/state/analysis/analysisExtraReducers.js
// Purpose: Defines how the slice responds to pending/fulfilled/rejected states from each thunk.
// Desired outcome: Keeps the main slice file small by grouping all "extraReducers" logic here.

import {
  bootstrapAnalysisData,
  fetchUploadedFiles,
  uploadFile,
  deleteFile,
  selectFile,
  fetchFileColumns,
  fetchReports,
  fetchReportById,
  generateReport,
  exportReportPdf,
  exportReportExcel,
  deleteReport,
  fetchSuggestedMappings,
  confirmColumnMappings,
  fetchConfirmedMappings,
  fetchFieldMappings,
  fetchSuggestedPartMatches,
  setConfidenceThreshold,
  storeManualMatches,
  fetchConfirmedPartMatches,
  confirmPartMatches,
  fetchContextualizedParts,
  confirmContextualizedParts,
  fetchAllOemParts,
  fetchCalculations,
  checkCalculationReadiness, // Import the new thunks
  runCalculations,
  runCalculationsScenarioC, // Import the scenario C specific thunk
  confirmCalculations,
  getCalculationResults,
  calculateValueSavings,
  getValueSavingsAnalysis
} from './analysisThunks';

// This function is called inside createSlice.extraReducers to link each thunk
function buildExtraReducers(builder) {
  // Handle bootstrapAnalysisData thunk
  builder
    .addCase(bootstrapAnalysisData.pending, (state) => {
      state.bootstrapInProgress = true;
      state.bootstrapComplete = false;
      state.bootstrapError = null;
    })
    .addCase(bootstrapAnalysisData.fulfilled, (state, action) => {
      if (!action.payload.alreadyCompleted) {
        state.availableCategories = action.payload.categories;
        state.partMatches = action.payload.partMatches;
      }
      state.bootstrapInProgress = false;
      state.bootstrapComplete = true;
    })
    .addCase(bootstrapAnalysisData.rejected, (state, action) => {
      state.bootstrapInProgress = false;
      state.bootstrapComplete = false;
      state.bootstrapError = action.payload || "Bootstrap process failed.";
    });

  // Handle fetchUploadedFiles
  builder
    .addCase(fetchUploadedFiles.pending, (state) => {
      state.progress.status = 'loading';
    })
    .addCase(fetchUploadedFiles.fulfilled, (state, action) => {
      state.uploadedFiles = action.payload;
      state.progress.status = 'succeeded';
    })
    .addCase(fetchUploadedFiles.rejected, (state, action) => {
      state.error = action.payload || { message: 'Failed to fetch files' };
      state.progress.status = 'failed';
    });

  // Handle uploadFile
  builder
    .addCase(uploadFile.pending, (state) => {
      state.progress.status = 'loading';
    })
    .addCase(uploadFile.fulfilled, (state, action) => {
      // Add newly uploaded file to the existing array
      state.uploadedFiles.push(action.payload);
      state.progress.status = 'succeeded';
    })
    .addCase(uploadFile.rejected, (state, action) => {
      state.error = action.payload || { message: 'File upload failed' };
      state.progress.status = 'failed';
    });

  // Handle deleteFile
  builder
    .addCase(deleteFile.pending, (state) => {
      state.progress.status = 'loading';
    })
    .addCase(deleteFile.fulfilled, (state, action) => {
      const fileID = action.payload;
      // Filter out the deleted file from array
      state.uploadedFiles = state.uploadedFiles.filter(
        (file) => file.fileID !== fileID
      );
      state.progress.status = 'succeeded';
    })
    .addCase(deleteFile.rejected, (state, action) => {
      state.error = action.payload || { message: 'File deletion failed' };
      state.progress.status = 'failed';
    });

  // Handle selectFile
  builder
    .addCase(selectFile.pending, (state) => {
      state.progress.status = 'loading';
      state.progress.currentStep = 'Selecting file';
      state.progress.percentComplete = 25;
    })
    .addCase(selectFile.fulfilled, (state, action) => {
      // The backend might return { file: {...}, columns: [...] }
      state.currentFile = action.payload.file;
      state.extractedColumns = action.payload.columns;
      // Move to next step in your workflow
      state.step = 'columnMapping';
      state.progress.status = 'succeeded';
      state.progress.currentStep = 'File selected';
      state.progress.percentComplete = 50;
    })
    .addCase(selectFile.rejected, (state, action) => {
      state.error = action.payload || { message: 'File selection failed' };
      state.progress.status = 'failed';
      state.progress.currentStep = '';
    });

  // Handle fetchFileColumns
  builder
    .addCase(fetchFileColumns.pending, (state) => {
      state.progress.status = 'loading';
    })
    .addCase(fetchFileColumns.fulfilled, (state, action) => {
      state.extractedColumns = action.payload.columns;
      state.progress.status = 'succeeded';
    })
    .addCase(fetchFileColumns.rejected, (state, action) => {
      state.error = action.payload || { message: 'Failed to fetch file columns' };
      state.progress.status = 'failed';
    });

  // Handle fetchSuggestedMappings
  builder
    .addCase(fetchSuggestedMappings.pending, (state) => {
      state.progress.status = 'loading';
      state.progress.currentStep = 'Generating column mapping suggestions';
    })
    .addCase(fetchSuggestedMappings.fulfilled, (state, action) => {
      state.columnMappings.suggested = action.payload;
      state.progress.status = 'succeeded';
      state.progress.currentStep = 'Mapping suggestions ready';
    })
    .addCase(fetchSuggestedMappings.rejected, (state, action) => {
      state.error = action.payload || { message: 'Failed to generate mapping suggestions' };
      state.progress.status = 'failed';
    });

  // Handle confirmColumnMappings
  builder
    .addCase(confirmColumnMappings.pending, (state) => {
      state.progress.status = 'loading';
      state.progress.currentStep = 'Confirming column mappings';
    })
    .addCase(confirmColumnMappings.fulfilled, (state, action) => {
      state.columnMappings.confirmed = action.payload;
      state.step = 'partMatching';
      state.progress.status = 'succeeded';
      state.progress.currentStep = 'Column mappings confirmed';
      state.progress.percentComplete = 60;
    })
    .addCase(confirmColumnMappings.rejected, (state, action) => {
      state.error = action.payload || { message: 'Failed to confirm column mappings' };
      state.progress.status = 'failed';
    });

  // Handle fetchConfirmedMappings
  builder
    .addCase(fetchConfirmedMappings.pending, (state) => {
      state.progress.status = 'loading';
    })
    .addCase(fetchConfirmedMappings.fulfilled, (state, action) => {
      state.columnMappings.confirmed = action.payload;
      state.progress.status = 'succeeded';
    })
    .addCase(fetchConfirmedMappings.rejected, (state, action) => {
      state.error = action.payload || { message: 'Failed to fetch confirmed mappings' };
      state.progress.status = 'failed';
    });

  // Handle fetchReports
  builder
    .addCase(fetchReports.pending, (state) => {
      state.progress.status = 'loading';
    })
    .addCase(fetchReports.fulfilled, (state, action) => {
      state.reports = action.payload;
      state.progress.status = 'succeeded';
    })
    .addCase(fetchReports.rejected, (state, action) => {
      state.error = action.payload || { message: 'Failed to fetch reports' };
      state.progress.status = 'failed';
    });

  // Handle fetchReportById
  builder
    .addCase(fetchReportById.pending, (state) => {
      state.progress.status = 'loading';
    })
    .addCase(fetchReportById.fulfilled, (state, action) => {
      // The entire loaded report data is stored
      state.reportData = action.payload;
      state.progress.status = 'succeeded';
    })
    .addCase(fetchReportById.rejected, (state, action) => {
      state.error = action.payload || { message: 'Failed to fetch report details' };
      state.progress.status = 'failed';
    });

  // Handle generateReport
  builder
    .addCase(generateReport.pending, (state) => {
      state.reportData.generating = true;
      state.reportData.error = null;
    })
    .addCase(generateReport.fulfilled, (state, action) => {
      state.reportData = {
        ...state.reportData,
        ...action.payload,
        generating: false,
        error: null,
        generatedAt: new Date().toISOString()
      };
      const reportExists = state.reports.some(r => r.reportID === action.payload.reportID);
      if (!reportExists && action.payload.reportID) {
        state.reports.push({
          reportID: action.payload.reportID,
          name: action.payload.name || `Report ${state.reports.length + 1}`,
          fileID: action.payload.fileID,
          generationDate: state.reportData.generatedAt
        });
      }
    })
    .addCase(generateReport.rejected, (state, action) => {
      state.reportData.generating = false;
      state.reportData.error = action.payload?.message || 'Failed to generate report';
    });

  // Handle exportReportPdf
  builder
    .addCase(exportReportPdf.pending, (state) => {
      state.reportData.exporting = true;
    })
    .addCase(exportReportPdf.fulfilled, (state, action) => {
      state.reportData.exporting = false;
      state.reportData.downloadURLs = {
        ...state.reportData.downloadURLs,
        pdf: action.payload.url
      };
    })
    .addCase(exportReportPdf.rejected, (state, action) => {
      state.reportData.exporting = false;
      state.reportData.error = action.payload?.message || 'Failed to export PDF';
    });

  // Handle exportReportExcel
  builder
    .addCase(exportReportExcel.pending, (state) => {
      state.reportData.exporting = true;
    })
    .addCase(exportReportExcel.fulfilled, (state, action) => {
      state.reportData.exporting = false;
      state.reportData.downloadURLs = {
        ...state.reportData.downloadURLs,
        excel: action.payload.url
      };
    })
    .addCase(exportReportExcel.rejected, (state, action) => {
      state.reportData.exporting = false;
      state.reportData.error = action.payload?.message || 'Failed to export Excel file';
    });

  // Handle deleteReport
  builder
    .addCase(deleteReport.pending, (state) => {
      state.progress.status = 'loading';
    })
    .addCase(deleteReport.fulfilled, (state, action) => {
      // Remove the deleted report from "reports" array
      state.reports = state.reports.filter(
        (report) => report.reportID !== action.payload
      );
      state.progress.status = 'succeeded';
    })
    .addCase(deleteReport.rejected, (state, action) => {
      state.error = action.payload || { message: 'Failed to delete report' };
      state.progress.status = 'failed';
    });

  // Handle fetchFieldMappings
  builder
    .addCase(fetchFieldMappings.pending, (state) => {
      state.progress.status = 'loading';
    })
    .addCase(fetchFieldMappings.fulfilled, (state, action) => {
      state.fieldMappings = action.payload;
      state.progress.status = 'succeeded';
    })
    .addCase(fetchFieldMappings.rejected, (state, action) => {
      state.error = action.payload || { message: 'Failed to fetch field mappings' };
      state.progress.status = 'failed';
    });

  // Part Matching extraReducers - Module 6
  builder
    .addCase(fetchSuggestedPartMatches.pending, (state) => {
      if (state.loadingPartMatches) return; // ✅ Prevent multiple concurrent fetches
      state.loadingPartMatches = true;
      state.progress.status = 'loading';
      state.progress.currentStep = 'Generating part matches';
    })
    .addCase(fetchSuggestedPartMatches.fulfilled, (state, action) => {
      state.loadingPartMatches = false; // ✅ Mark as completed
      state.partMatches = action.payload; // ✅ Store fetched data
      state.progress.status = 'succeeded';
      state.progress.currentStep = 'Part matches generated';
    })
    .addCase(fetchSuggestedPartMatches.rejected, (state, action) => {
      console.error("❌ Error fetching part matches:", action.error.message);
      state.loadingPartMatches = false; // ✅ Stop infinite re-fetching
      state.error = action.payload || { message: 'Failed to generate part matches' };
      // ❌ Do NOT reset `state.partMatches` here - prevents refetch loop!
      state.progress.status = 'failed';
    });

  // Handle fetchAllOemParts
  builder
    .addCase(fetchAllOemParts.pending, (state) => {
      state.loadingOemParts = true;
    })
    .addCase(fetchAllOemParts.fulfilled, (state, action) => {
      state.oemParts = action.payload;
      state.loadingOemParts = false;
    })
    .addCase(fetchAllOemParts.rejected, (state, action) => {
      state.loadingOemParts = false;
      state.error = action.payload || { message: 'Failed to fetch OEM parts' };
    });

  builder
    .addCase(setConfidenceThreshold.pending, (state) => {
      state.progress.status = 'loading';
    })
    .addCase(setConfidenceThreshold.fulfilled, (state, action) => {
      state.confidenceThreshold = action.payload;
      state.progress.status = 'succeeded';
    })
    .addCase(setConfidenceThreshold.rejected, (state, action) => {
      state.error = action.payload || { message: 'Failed to set confidence threshold' };
      state.progress.status = 'failed';
    });

  builder
    .addCase(storeManualMatches.pending, (state) => {
      state.progress.status = 'loading';
    })
    .addCase(storeManualMatches.fulfilled, (state, action) => {
      // Update the matching parts in the state with the manual matches
      action.payload.forEach(manualMatch => {
        const index = state.partMatches.findIndex(p => p.partID === manualMatch.partID);
        if (index !== -1) {
          state.partMatches[index].matchedCategory = manualMatch.matchedCategory;
          state.partMatches[index].status = manualMatch.status || 'confirmed';
        }
      });
      state.progress.status = 'succeeded';
    })
    .addCase(storeManualMatches.rejected, (state, action) => {
      state.error = action.payload || { message: 'Failed to store manual matches' };
      state.progress.status = 'failed';
    });

  builder
    .addCase(fetchConfirmedPartMatches.pending, (state) => {
      state.progress.status = 'loading';
    })
    .addCase(fetchConfirmedPartMatches.fulfilled, (state, action) => {
      state.partMatches = action.payload;
      state.progress.status = 'succeeded';
    })
    .addCase(fetchConfirmedPartMatches.rejected, (state, action) => {
      state.error = action.payload || { message: 'Failed to fetch confirmed part matches' };
      state.progress.status = 'failed';
    });

  builder
    .addCase(confirmPartMatches.pending, (state) => {
      state.progress.status = 'loading';
      state.progress.currentStep = 'Confirming part matches';
    })
    .addCase(confirmPartMatches.fulfilled, (state, action) => {
      state.partMatches = action.payload;
      state.step = 'partContext';
      state.progress.status = 'succeeded';
      state.progress.currentStep = 'Part matches confirmed';
      state.progress.percentComplete = 70;
    })
    .addCase(confirmPartMatches.rejected, (state, action) => {
      state.error = action.payload || { message: 'Failed to confirm part matches' };
      state.progress.status = 'failed';
    });

  // Part Context extraReducers - Module 7
  builder
    .addCase(fetchContextualizedParts.pending, (state) => {
      state.progress.status = 'loading';
      state.progress.currentStep = 'Contextualizing parts';
    })
    .addCase(fetchContextualizedParts.fulfilled, (state, action) => {
      state.contextualizedParts = action.payload;
      state.progress.status = 'succeeded';
      state.progress.currentStep = 'Parts contextualized';
    })
    .addCase(fetchContextualizedParts.rejected, (state, action) => {
      state.error = action.payload || { message: 'Failed to contextualize parts' };
      state.progress.status = 'failed';
    });

  builder
    .addCase(confirmContextualizedParts.pending, (state) => {
      state.progress.status = 'loading';
      state.progress.currentStep = 'Confirming contextualized parts';
    })
    .addCase(confirmContextualizedParts.fulfilled, (state, action) => {
      state.contextualizedParts = action.payload;
      state.step = 'calculations';
      state.progress.status = 'succeeded';
      state.progress.currentStep = 'Parts contextualization confirmed';
      state.progress.percentComplete = 80;
    })
    .addCase(confirmContextualizedParts.rejected, (state, action) => {
      state.error = action.payload || { message: 'Failed to confirm contextualized parts' };
      state.progress.status = 'failed';
    });

  // Calculations Extra Reducers Module 8

  // Handle fetchCalculations
  builder
    .addCase(fetchCalculations.pending, (state) => {
      state.calculations.loading = true;
      state.calculations.error = null;
    })
    .addCase(fetchCalculations.fulfilled, (state, action) => {
      state.calculations.data = action.payload;
      state.calculations.loading = false;
    })
    .addCase(fetchCalculations.rejected, (state, action) => {
      state.calculations.loading = false;
      state.calculations.error = action.payload || "Failed to fetch calculations.";
    });

    // Handle checkCalculationReadiness
    builder
    .addCase(checkCalculationReadiness.pending, (state) => {
      state.calculations.loading = true;
      state.calculations.error = null;
    })
    .addCase(checkCalculationReadiness.fulfilled, (state, action) => {
      state.calculations.readinessCheck = action.payload;
      state.calculations.loading = false;
    })
    .addCase(checkCalculationReadiness.rejected, (state, action) => {
      state.calculations.loading = false;
      state.calculations.error = action.payload || "Failed to check calculation readiness.";
    });

  // Handle runCalculations
  builder
    .addCase(runCalculations.pending, (state) => {
      state.calculations.loading = true;
      state.calculations.error = null;
    })
    .addCase(runCalculations.fulfilled, (state, action) => {
      state.calculations.data = action.payload;
      state.calculations.loading = false;
      
      // If scenario_C results are included in the payload, update scenario_C state
      if (action.payload && action.payload.summary && action.payload.summary.scenario_C) {
        state.calculations.scenario_C.data = action.payload.summary.scenario_C;
        state.calculations.scenario_C.loading = false;
        state.calculations.scenario_C.error = null;
      }
    })
    .addCase(runCalculations.rejected, (state, action) => {
      state.calculations.loading = false;
      state.calculations.error = action.payload || "Failed to run calculations.";
    });
    
  // Handle runCalculationsScenarioC
  builder
    .addCase(runCalculationsScenarioC.pending, (state) => {
      state.calculations.scenario_C.loading = true;
      state.calculations.scenario_C.error = null;
    })
    .addCase(runCalculationsScenarioC.fulfilled, (state, action) => {
      state.calculations.scenario_C.data = action.payload;
      state.calculations.scenario_C.loading = false;
    })
    .addCase(runCalculationsScenarioC.rejected, (state, action) => {
      state.calculations.scenario_C.loading = false;
      state.calculations.scenario_C.error = action.payload || "Failed to run carbon footprint calculations.";
    });

  // Handle confirmCalculations
  builder
    .addCase(confirmCalculations.pending, (state) => {
      state.calculations.loading = true;
    })
    .addCase(confirmCalculations.fulfilled, (state, action) => {
      state.calculations.status = 'confirmed';
      state.calculations.loading = false;
      state.step = 'valueSavingsAnalysis';
      state.progress.currentStep = 'Calculations confirmed';
      state.progress.percentComplete = 85;
    })
    .addCase(confirmCalculations.rejected, (state, action) => {
      state.calculations.loading = false;
      state.calculations.error = action.payload || "Failed to confirm calculations.";
    });

  // Handle getCalculationResults
  builder
    .addCase(getCalculationResults.pending, (state) => {
      state.calculations.loading = true;
      state.calculations.error = null;
    })
    .addCase(getCalculationResults.fulfilled, (state, action) => {
      state.calculations.data = action.payload;
      state.calculations.loading = false;
      
      // If scenario_C results are included in the payload, update scenario_C state
      if (action.payload && action.payload.summary && action.payload.summary.scenario_C) {
        state.calculations.scenario_C.data = action.payload.summary.scenario_C;
      }
    })
    .addCase(getCalculationResults.rejected, (state, action) => {
      state.calculations.loading = false;
      state.calculations.error = action.payload || "Failed to fetch calculation results.";
    });

  // Handle value savings calculations
  builder
    .addCase(calculateValueSavings.pending, (state) => {
      state.valueSavings.loading = true;
      state.valueSavings.error = null;
    })
    .addCase(calculateValueSavings.fulfilled, (state, action) => {
      state.valueSavings.data = action.payload;
      state.valueSavings.loading = false;
      state.step = 'reportGeneration'; // Move to report generation after value savings
      state.progress.currentStep = 'Value Savings Analysis completed';
      state.progress.percentComplete = 95;
    })
    .addCase(calculateValueSavings.rejected, (state, action) => {
      state.valueSavings.loading = false;
      state.valueSavings.error = action.payload || { message: 'Failed to calculate value savings' };
    });

  // Handle getting value savings analysis
  builder
    .addCase(getValueSavingsAnalysis.pending, (state) => {
      state.valueSavings.loading = true;
      state.valueSavings.error = null;
    })
    .addCase(getValueSavingsAnalysis.fulfilled, (state, action) => {
      if (action.payload) {
        state.valueSavings.data = action.payload.results;
        state.valueSavings.scenarioType = action.payload.scenarioType;
        state.valueSavings.assumptions = action.payload.assumptions;
      }
      state.valueSavings.loading = false;
    })
    .addCase(getValueSavingsAnalysis.rejected, (state, action) => {
      state.valueSavings.loading = false;
      state.valueSavings.error = action.payload || { message: 'Failed to fetch value savings analysis' };
    });
}

export default buildExtraReducers;